import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'

import type {
    Categoria,
    GaleriaItem,
    LayoutData,
    Material,
    ProductoResumen
} from '@/types'

import { Seo } from '@/components/generals/seo'
import CtaBanner from '@/components/generals/cta-banner'
import Hero from '@/components/landing/hero'
import CategoriesSection from '@/components/landing/categories-section'
import FeaturedProducts from '@/components/landing/featured-products'
import CustomizationSection from '@/components/landing/customization-section'
import MaterialsSection from '@/components/landing/materials-section'
import InspirationSection from '@/components/landing/inspiration-section'
import QuoteProcess from '@/components/landing/quote-process'

import {
    getCategorias,
    getGaleria,
    getLayoutData,
    getMateriales,
    getProductos,
    safe
} from '@/lib/catalog'

import { revalidateSeconds } from '@/constants/site'

interface HomeProps {
    layout: LayoutData
    categorias: Array<Categoria>
    destacados: Array<ProductoResumen>
    materiales: Array<Material>
    galeria: Array<GaleriaItem>
}

const Home: FC<HomeProps> = ({ categorias, destacados, materiales, galeria }): ReactNode => {
    return (
        <>
            <Seo description='Placas, trofeos, medallas y reconocimientos personalizados en Ecuador. Piezas de latón, acero, bronce, cristal y madera para premiar logros empresariales, deportivos, académicos e institucionales.' />

            <Hero />
            <CategoriesSection categorias={categorias} />
            <FeaturedProducts productos={destacados} />
            <CustomizationSection />
            <MaterialsSection materiales={materiales} />
            <InspirationSection galeria={galeria} />
            <QuoteProcess />
            <CtaBanner />
        </>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    const [layout, categorias, destacados, materiales, galeria] = await Promise.all([
        getLayoutData(),
        safe('categorias', getCategorias, []),
        safe('destacados', () => getProductos({ destacados: true, limit: 6 }), []),
        safe('materiales', getMateriales, []),
        safe('galeria', () => getGaleria(), [])
    ])

    return {
        props: {
            layout,
            categorias,
            destacados,
            materiales,
            galeria
        },
        revalidate: revalidateSeconds
    }
}

export default Home
